import { useState } from 'react';
import { Plus, Pencil, Check, X } from 'lucide-react';
import type { SalesTeam, TeamAssignment } from '../../types/salesTeamConfig';

interface ManageTeamsProps {
    teams: SalesTeam[];
    assignments: TeamAssignment[];
    canEdit: boolean;
    onCreateTeam: (name: string, description: string | null) => Promise<void>;
    onUpdateTeam: (teamId: number, name: string, description: string | null, isActive?: boolean) => Promise<void>;
}

export function ManageTeams({ teams, assignments, canEdit, onCreateTeam, onUpdateTeam }: ManageTeamsProps) {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Count members per team
    const memberCount = new Map<number, number>();
    for (const a of assignments) {
        memberCount.set(a.SalesTeamID, (memberCount.get(a.SalesTeamID) || 0) + 1);
    }

    const handleCreate = async () => {
        const name = newName.trim();
        if (!name) return;

        setCreating(true);
        setError(null);
        try {
            await onCreateTeam(name, newDescription.trim() || null);
            setNewName('');
            setNewDescription('');
            setShowCreateForm(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create team');
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (team: SalesTeam) => {
        setEditingId(team.SalesTeamID);
        setEditName(team.SalesTeamName);
        setEditDescription(team.SalesTeamDescription || '');
        setError(null);
    };

    const handleSave = async (teamId: number) => {
        const name = editName.trim();
        if (!name) return;

        setSaving(true);
        setError(null);
        try {
            await onUpdateTeam(teamId, name, editDescription.trim() || null);
            setEditingId(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update team');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Create new team */}
            {canEdit && (
                <div>
                    {showCreateForm ? (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                            <h3 className="text-sm font-semibold text-gray-900">Create New Team</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder="Team name *"
                                    value={newName}
                                    onChange={e => setNewName(e.target.value)}
                                    className="rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <input
                                    type="text"
                                    placeholder="Description (optional)"
                                    value={newDescription}
                                    onChange={e => setNewDescription(e.target.value)}
                                    className="rounded-md border border-gray-300 py-2 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleCreate}
                                    disabled={!newName.trim() || creating}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Plus className="h-4 w-4" />
                                    {creating ? 'Creating...' : 'Create Team'}
                                </button>
                                <button
                                    onClick={() => { setShowCreateForm(false); setNewName(''); setNewDescription(''); setError(null); }}
                                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                        >
                            <Plus className="h-4 w-4" />
                            New Team
                        </button>
                    )}
                </div>
            )}

            {/* Teams list */}
            <div className="rounded-lg border border-gray-200 bg-white">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                    <h3 className="text-sm font-semibold text-gray-900">
                        All Teams ({teams.length})
                    </h3>
                </div>
                <div className="divide-y divide-gray-100">
                    {teams.map(team => (
                        <div key={team.SalesTeamID} className="px-4 py-3">
                            {editingId === team.SalesTeamID ? (
                                <div className="space-y-2">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <input
                                            type="text"
                                            value={editName}
                                            onChange={e => setEditName(e.target.value)}
                                            className="rounded-md border border-gray-300 py-1.5 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                        <input
                                            type="text"
                                            value={editDescription}
                                            onChange={e => setEditDescription(e.target.value)}
                                            placeholder="Description (optional)"
                                            className="rounded-md border border-gray-300 py-1.5 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => handleSave(team.SalesTeamID)}
                                            disabled={!editName.trim() || saving}
                                            className="inline-flex items-center gap-1 rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                        >
                                            <Check className="h-3.5 w-3.5" />
                                            {saving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={() => { setEditingId(null); setError(null); }}
                                            className="inline-flex items-center gap-1 rounded bg-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                        <span className="font-medium text-gray-900">
                                            {team.SalesTeamName}
                                        </span>
                                        {team.SalesTeamDescription && (
                                            <span className="ml-2 text-sm text-gray-500">
                                                — {team.SalesTeamDescription}
                                            </span>
                                        )}
                                    </div>
                                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                        {memberCount.get(team.SalesTeamID) || 0} members
                                    </span>
                                    {canEdit && (
                                        <button
                                            onClick={() => startEdit(team)}
                                            title="Edit team"
                                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
